import loginService from '../login.service';
import crmOpportunityFiltersService from './crm.opportunity.filters.service';
import crmFiltersService from './crm.filters.service';
import crmOpportunityScheduleService from './crm.opportunity.schedule.service';

import repository from '../../repositories/crm/crm.opportunity.repository';
import { isDiscardableContact } from '../../constant/crm/crm.contact.constant';
import { markAsLostTexts } from '../../constant/crm/opportunity/crm.opportunity.mark.as.lost.constant';
import { assumeFromNotificationParam, assumeTexts } from '../../constant/crm/opportunity/crm.opportunity.assume.constant';
import { scheduleOrigins, scheduleTexts } from '../../constant/crm/opportunity/crm.opportunity.schedule.constant';
import { conversationHistoryTexts } from '../../constant/crm/crm.conversation.constant';

const findById = (opportunityId) =>
    repository
        .findById(opportunityId)
        .then(res => res.data || {});

const findDetailsById = (opportunityId) =>
    repository
        .findDetailsById(opportunityId)
        .then(mapOpportunityInfo);

const mapOpportunityInfo = (res) => {

    const { statusTimeline = [], currentStatusActivities = {} } = res.data || {};

    return {
        statusTimeline,
        currentStatusActivities
    }
}

const requestOpportunity = async (values) =>
    repository
        .request(values)
        .then(res => res.data || {});

const createFromReferral = async (values) =>
    repository
        .createFromReferral(values)
        .then(res => res.data || {});

const checkUserIsLinkedToOpportunity = (opportunity = {}) => {
    const currentUser = loginService.getUserAuthentication();
    return currentUser?.sellerId === opportunity.seller?.id;
}

const update = (opportunityId, values) =>
    repository
        .update(opportunityId, values)
        .then(res => res.data || {});

const lose = (opportunityId, data) =>
    repository
        .lose(opportunityId, data)
        .then(res => res.data || {});

const remove = (id, data = {}) =>
    repository
        .remove(id, data)
        .then(res => res?.data || {});

const findWithFilterBySellerId = (filters, pagination) => {

    const formattedFilters = crmFiltersService.formatFilters(filters);
    const currentUserSellerId = loginService.getUserAuthentication()?.sellerId;
    const formattedFiltersWithSellerId = [...formattedFilters, { name: 'sellerId', value: currentUserSellerId }];

    return findByFilter(formattedFiltersWithSellerId, pagination);
}

const findByFilter = (filters, pagination) =>
    repository
        .findByFilter(filters, pagination)
        .then(res => res.data || []);

const findFilters = () =>
    repository
        .findFilters()
        .then(res => crmOpportunityFiltersService.getFiltersWithExtraInfo(res.data));

const findOperationConfig = async () =>
    crmOpportunityScheduleService.findOperationConfig();

const findConversation = (opportunityId) =>
    repository
        .findOrbitaConversation(opportunityId)
        .then(res => res.data?.value || null);

const findConversationHistory = (opportunityId) =>
    repository
        .findOrbitaHistory(opportunityId)
        .then(res => res.data?.value || null);

const registerSentMessage = (opportunityId, { messageId = null, body = null, activityId = null } = {}) =>
    repository
        .registerOrbitaSentMessage(opportunityId, { messageId, body, activityId })
        .then(res => res.data?.value || null);

const operations = {
    schedule: {
        label: scheduleTexts.operationLabel,
        key: 'schedule',
        variant: 'outlined',
        color: 'primary',
        group: 'main',
        validator: crmOpportunityScheduleService.canSchedule,
    },
    viewConversationHistory: {
        label: conversationHistoryTexts.actionLabel,
        key: 'viewConversationHistory',
        variant: 'outlined',
        group: 'secondary',
        validator: (opportunity) => !opportunity.concludedAt && checkUserIsLinkedToOpportunity(opportunity)
    },
    cancelSchedule: {
        label: scheduleTexts.cancelOperationLabel,
        key: 'cancelSchedule',
        variant: 'outlined',
        action: (opportunity, { origin = scheduleOrigins.detailMenu } = {}) =>
            crmOpportunityScheduleService.cancelSchedule(opportunity.id, { origin }),
        group: 'secondary',
        validator: crmOpportunityScheduleService.canCancelSchedule,
        successMessage: scheduleTexts.cancelSuccessMessage,
        confirmation: {
            severity: 'warning',
            title: scheduleTexts.cancelConfirmationTitle,
            description: scheduleTexts.cancelConfirmationDescription,
        },
    },
    talkToContact: {
        label: 'Falar com contato',
        key: 'talkToContact',
        variant: 'outlined',
        color: 'primary',
        group: 'main',
        validator: (opportunity) => !opportunity.concludedAt && checkUserIsLinkedToOpportunity(opportunity)
    },
    edit: {
        label: 'Editar',
        key: 'edit',
        variant: 'outlined',
        group: 'main',
        color: 'primary',
        validator: checkUserIsLinkedToOpportunity,
    },
    markAsLost: {
        label: markAsLostTexts.label,
        key: 'markAsLost',
        variant: 'outlined',
        action: (opportunity, data) => lose(opportunity.id, data),
        group: 'secondary',
        validator: (opportunity) => !opportunity.concludedAt && checkUserIsLinkedToOpportunity(opportunity),
        successMessage: markAsLostTexts.successMessage,
    },
    delete: {
        label: 'Descartar',
        key: 'delete',
        variant: 'contained',
        action: (opportunity, data) => remove(opportunity.id, data),
        group: 'secondary',
        validator: (opportunity) => !opportunity.isRelationshipPipeline
            && isDiscardableContact(opportunity.contact || {})
            && (!opportunity.seller?.id || checkUserIsLinkedToOpportunity(opportunity)),
        successMessage: 'Oportunidade excluída com sucesso!',
        successRedirectPath: '/crm/agenda',
        getDataModalInfo: () => ({
            title: 'Motivo do descarte',
            asyncFindFunction: findOperationConfig,
            fields: [
                {
                    key: 'reason',
                    label: 'Motivo do descarte',
                    type: 'select',
                    getDynamicProps: (config) => ({ options: (config.discardReasons || []).map(reason => ({ label: reason })) }),
                    required: true,
                },
                {
                    key: 'observation',
                    label: 'Observação',
                    type: 'multiline',
                }
            ]
        }),
        confirmation: {
            severity: 'warning',
            title: 'Tem certeza que deseja descartar a oportunidade?',
            description: 'Quando descartada, todas as atividades e informações relacionadas à oportunidade são perdidas. Essa opção só deve ser utilizada se for indentificado que o contato não possui perfil de cliente final.'
        },
    },
}

const isAssumeFromNotificationRequested = (search = '') =>
    new URLSearchParams(search).get(assumeFromNotificationParam) === 'true';

const assume = (opportunityId) =>
    repository
        .assume(opportunityId)
        .then(res => res.data || {});

const getAssumeFeedback = (outcome = {}) => {

    if (outcome.assumed)
        return { text: assumeTexts.assumed, severity: 'success' };

    if (outcome.alreadyYours)
        return { text: assumeTexts.alreadyYours, severity: 'info' };

    if (outcome.takenBy)
        return { text: assumeTexts.getTakenBy(outcome.takenBy.name), severity: 'warning' };

    return { text: outcome.reason, severity: 'warning' };
}

const getOpportunityOperations = (opportunity) =>
    Object.values(operations).filter(operation => !operation.validator || operation.validator(opportunity));

const getOperationByKey = (key) => operations[key];

export default {
    findById,
    findDetailsById,
    requestOpportunity,
    createFromReferral,
    checkUserIsLinkedToOpportunity,
    update,
    remove,
    findWithFilterBySellerId,
    findFilters,
    getOpportunityOperations,
    getOperationByKey,
    findConversation,
    findConversationHistory,
    registerSentMessage,
    isAssumeFromNotificationRequested,
    assume,
    getAssumeFeedback,
}
